import { useCallback, useRef } from 'react'
import {
    BOX_TRUCK_PRICING_REPORT_REASON_REQUIRING_NOTE,
    BOX_TRUCK_PRICING_REPORT_REASONS,
    BoxTruckPricingQuote,
    BoxTruckPricingRequest,
    MAX_PRICING_REPORT_MESSAGE,
    type BoxTruckPricingReportReason,
} from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import { ReportProblemDialog, type ReportProblemReason } from '@/components/shared/ReportProblemDialog'
import { reportBoxTruckPricingProblem } from '../services/boxTruckLoadsApi'
import { trackBoxTruckPricingReported } from '../utils/analytics'

/**
 * The contract's reasons in the shape the shared dialog takes.
 *
 * Built once at module level: the list never changes, and rebuilding it per render would hand the dialog
 * a new array every time the live board re-prices behind it.
 */
const REASON_OPTIONS: readonly ReportProblemReason<BoxTruckPricingReportReason>[] = BOX_TRUCK_PRICING_REPORT_REASONS.map(({ reason, label }) => ({
    value: reason,
    label,
    requiresNote: reason === BOX_TRUCK_PRICING_REPORT_REASON_REQUIRING_NOTE,
}))

/**
 * "This suggestion is wrong" — the pricing panel's one way to answer back.
 *
 * The panel is a model nobody has checked against outcomes yet, and the dispatcher reading it is the only
 * person who can say when it is off. What they pick, and anything they add to it, goes to the pricing
 * Telegram group with the load and the model's own numbers beside it; nothing is stored.
 *
 * The reasons come from the contract rather than from here, because the server prints their labels into
 * that message: a list declared on this side would be a second copy of the one the server reads, and the
 * day a wording changed only one of them would move.
 *
 * It sends the quote REQUEST rather than the figures on screen, and the server re-prices from it — the
 * same rule the decision record keeps, so a report can be compared against the model later instead of
 * being a screenshot in words.
 *
 * `quote` and `request` are a SNAPSHOT taken when the dialog opened, not the panel's live state, and the
 * panel mounts this OUTSIDE its status switch. The board is live: a broker reposting a rate changes
 * `priceKey`, which puts the panel back into `loading` — and while this sat inside the `ready` branch
 * that unmounted the dialog and took a half-written complaint with it, silently, which is the one thing
 * the dialog promises not to do. The complaint is about what the dispatcher SAW, so that is what it
 * carries and that is what survives a re-price.
 */
export const BoxTruckPricingReportDialog = ({
    load,
    request,
    quote,
    onClose,
}: {
    load: CanonicalLoad
    request: BoxTruckPricingRequest
    quote: BoxTruckPricingQuote
    onClose: () => void
}) => {
    /**
     * Whether anything actually reached the group, so closing afterwards is not counted as giving up.
     *
     * A ref and not state: the dialog closes on the same tick it succeeds, and a re-render in between is
     * not something the outcome can depend on.
     */
    const sent = useRef(false)

    const submit = useCallback(
        async (message: string, picked?: BoxTruckPricingReportReason) => {
            try {
                await reportBoxTruckPricingProblem({
                    externalId: load.externalId ?? String(load.id ?? ''),
                    sourceType: load.sourceType ?? '',
                    quote: request,
                    originCity: load.pickupCity ?? undefined,
                    destinationCity: load.deliveryCity ?? undefined,
                    reason: picked,
                    // Omitted rather than sent empty: under a picked reason the words are optional, and an
                    // empty string would print an empty `Description:` in the group's message.
                    message: message || undefined,
                })
            } catch (error) {
                // Counted and then rethrown, so the dialog still shows the failure and keeps the text.
                // Nothing is stored anywhere, so an uncounted failure is a feedback channel that can go
                // dead — Telegram down, no group configured, the tenant's model gone — with the panel
                // looking exactly as it does when nobody has a complaint.
                trackBoxTruckPricingReported('failed', quote.equipment, quote.confidence, picked)
                throw error
            }
            sent.current = true
            trackBoxTruckPricingReported('sent', quote.equipment, quote.confidence, picked)
        },
        [load, request, quote]
    )

    /** Closed without anything going out — someone opened it, thought again, and that is worth knowing. */
    const close = useCallback(() => {
        if (!sent.current) trackBoxTruckPricingReported('cancelled', quote.equipment, quote.confidence)
        onClose()
    }, [onClose, quote])

    return (
        <ReportProblemDialog
            open
            onClose={close}
            onSubmit={submit}
            title="Report a pricing problem"
            description="What is wrong with this suggestion? The load and the numbers it was priced from are sent with it."
            reasonOptions={REASON_OPTIONS}
            placeholder="Anything to add? e.g. brokers on this lane start around $1,100"
            successMessage="Thanks — the pricing team has it"
            // The server's own cap, from the contract both sides read, so the box stops where the
            // truncation would have started rather than dropping the tail of a paste and reporting success.
            maxLength={MAX_PRICING_REPORT_MESSAGE}
        />
    )
}
